import { Container, Paper, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Perspectives } from './lead-questionnaire.types';

interface Props {
  perspective?: Perspectives;
  language: string;

  handleQuestionnaireLangChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleStartQuestionnaireClick(): void;
}

const QuestionnaireIntroduction: React.FC<Props> = ({ perspective, language, handleQuestionnaireLangChange, handleStartQuestionnaireClick }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const leadLogo = '/dashboard/images/lead/lead_logo.png';
  const options = [
    { label: t('german'), value: 'de' },
    { label: t('english'), value: 'en' },
  ];

  return (
    <Container maxWidth={'lg'}>
      <Stack gap={4}>
        <Typography className={classes.title}>{t('leadQuestionnaireIntroduction')}</Typography>
        <Stack direction={'row'} justifyContent={'center'}>
          <img className={classes.logo} src={leadLogo} alt="Lead Logo" />
        </Stack>
        <Stack gap={2}>
          <Paper className={classes.textContainer} elevation={2} square={true}>
            <Stack gap={2}>
              {perspective === Perspectives.SELF ? (
                <>
                  <Typography className={classes.text}>{t('leadQuestionnaireIntroductionSelfPerspectiveText')}</Typography>
                </>
              ) : (
                <>
                  <Typography className={classes.text}>{t('leadQuestionnaireIntroductionOtherPerspectiveText')}</Typography>
                </>
              )}
              <Typography className={classes.text}>
                <Trans i18nKey={'leadQuestionnaireIntroductionText1'}>
                  Sollte Ihnen einmal die Entscheidung schwer fallen, kreuzen Sie trotzdem bitte die
                  <b>Antwort an, welche am ehesten auf die Frage zutrifft.</b> Falls Sie einmal eine unzutreffende Einschätzung getroffen haben,
                  klicken Sie einfach eine andere <b>Antwortalternative</b> an: die zuerst angeklickte Markierung verschwindet automatisch.
                </Trans>
              </Typography>
              <Typography className={classes.text}>
                <Trans i18nKey={'leadQuestionnaireIntroductionText2'}>
                  Auf den letzten Seiten des Fragebogens haben Sie die Möglichkeit, <b>Ihre persönlichen Eindrücke</b> mitzuteilen. Erfahrungsgemäß
                  sind gerade diese persönlichen Hinweise wichtige Indikatoren für die weitere persönliche Entwicklung. Wir bitten Sie daher, auch
                  hier Ihre Einschätzungen anzugeben. Vielen Dank!
                </Trans>
              </Typography>
              <Typography className={classes.text}>{t('leadQuestionnaireIntroductionText3')}</Typography>
              <PaiSelect
                value={language}
                disableBorder={true}
                backgroundColor={'#FFF'}
                onChange={handleQuestionnaireLangChange}
                style={{ width: '250px' }}
              >
                {options.map((option) => (
                  <PaiMenuItem key={option.value} value={option.value}>
                    {option.label}
                  </PaiMenuItem>
                ))}
              </PaiSelect>
            </Stack>
          </Paper>
          <Stack direction={'row'} justifyContent={'flex-end'}>
            <PaiButton text={t('leadQuestionnaireStartSurvey')} size={'large'} action={handleStartQuestionnaireClick} />
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};

const useStyles = makeStyles()((theme) => ({
  title: {
    fontSize: theme.typography.pxToRem(32),
    textAlign: 'center',
    fontWeight: 'bold',
  },
  logo: {
    width: '500px',
    height: 'auto',
    marginBottom: '16px',
  },
  textContainer: {
    padding: '24px 32px',
    background: 'rgba(0,0,0,0.03)',
  },
  text: {
    fontSize: theme.typography.pxToRem(18),
  },
}));

export default QuestionnaireIntroduction;
